<template>
  <div class="relative">
    <FormLabel :for="id" :required="required" :label="label" :label-hint="labelHint"/>
    <select
      :id="id"
      ref="selectEl"
      :style="'--background-color: rgba(' + (backgroundRGBA || '241, 245, 249, 1') + ');'"
      class="select leading-tight block w-full focus:ring-2 focus:ring-offset-2 focus:ring-bdBlue-200 focus:ring-offset-white"
      tabindex="0"
      :class="selectClasses"
      :required="required"
      :disabled="readonly"
      :value="selectedValue || ''"
      @update:modelValue="(e) => selectedValue = e"
      @change="selectOption"
    >
      <option
        v-if="!hidePlaceholder"
        disabled
        value=""
      >
        {{ placeholder || "Select" }}
      </option>
      <option
        v-for="(option, i) in options"
        :value="getOptionValue(option)"
        :class="{'capitalize': props.capitalize}"
        :key="i"
      >
        {{ prefix }}{{ getOptionName(option) }}
      </option>
    </select>
    <p v-if="error" class="absolute left-0 text-sm text-red-600 bottom-1">
      {{ error }}
    </p>
  </div>
</template>

<script setup>
import FormLabel from "./FormLabel.vue";
import {computed, ref, watch} from "vue";

const props = defineProps({
  forcedId: String,
  label: String,
  placeholder: String,
  hidePlaceholder: Boolean,
  modelValue: [String, Number, Object],
  required: Boolean,
  error: String,
  readonly: Boolean,
  options: Array,
  removeMargin: Boolean,
  roundedFull: Boolean,
  backgroundRGBA: String,
  capitalize: Boolean,
  arrowWhite: Boolean,
  labelHint: String,
  shouldParseInt: Boolean,
  falsyIsValid: Boolean,
  prefix: String,
  notRoundedRight: Boolean,
  paddingClasses: {
    type: String,
    default: "py-2.5 px-4"
  },
  textClasses: {
    type: String,
    default: "text-base"
  },
});
const emits = defineEmits(["update:modelValue", "select-index"]);

const selectEl = ref();

function selectOption(e) {
  if (props.readonly) e.preventDefault();
  emits("update:modelValue", props.shouldParseInt ? parseInt(e.target.value) : e.target.value);
  emits("select-index", parseInt(e.target.selectedIndex));
}

let id = props.forcedId || "select-" + Math.random().toString(36).substr(2, 9);
let selectedValue = ref("");

const selectClasses = computed(() => [
  "appearance-none focus:outline-none border leading-tight",
  props.arrowWhite ? "select-white-arrow text-white" : "text-slate-700",
  props.capitalize ? "capitalize" : "",
  props.roundedFull ? "rounded-full" : props.notRoundedRight ? "rounded-l" : "rounded-lg",
  props.error ? "border-red-500" : "border-slate-200",
  props.readonly ? "active:border-red-600 cursor-not-allowed" : "focus:bg-white focus:border-slate-500",
  (selectedValue.value == null || selectedValue.value === "") && !props.falsyIsValid ? "text-opacity-50" : "text-opacity-100",
  props.paddingClasses,
  props.textClasses,
]);

const getOptionValue = (option) => {
  return typeof option === "string" ? option : `${option.value}`;
};

const getOptionName = (option) => typeof option === "string" ? option : option.name;

watch(() => props.modelValue, () => selectedValue.value = (props.modelValue || props.falsyIsValid) ? `${props.modelValue}` : undefined, {immediate: true});

defineExpose({selectEl});
</script>

<style scoped>
.select {
  /* eslint-disable */
  background: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4Ljc5NiIgaGVpZ2h0PSI1LjgxMyIgdmlld0JveD0iMCAwIDguNzk2IDUuODEzIj48cGF0aCBkPSJNNjc2My40ODksMjA0MC44MjdsLTQuNC00LjQsMS40MTQtMS40MTQsMi45ODQsMi45ODQsMi45ODMtMi45ODQsMS40MTQsMS40MTRaIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtNjc1OS4wOTEgLTIwMzUuMDE1KSIgZmlsbD0iIzFjMjUzMyIvPjwvc3ZnPg==) no-repeat calc(100% - 10px);
  /* eslint-disable */
  background-color: var(--background-color);
  -moz-appearance: none;
  -webkit-appearance: none;
  appearance: none;
  width: calc(100% - 5px);
}

.select-white-arrow {
  /* eslint-disable */
  background: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4Ljc5NSIgaGVpZ2h0PSI1LjgxNCIgdmlld0JveD0iMCAwIDguNzk1IDUuODE0Ij48cGF0aCBkPSJNNjc2My40ODksMjA0MC44MjdsLTQuNC00LjQsMS40MTQtMS40MTQsMi45ODQsMi45ODQsMi45ODMtMi45ODQsMS40MTQsMS40MTRaIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtNjc1OS4wODkgLTIwMzUuMDEzKSIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==) no-repeat calc(100% - 10px);
  /* eslint-disable */
  background-color: var(--background-color);
}
</style>
